import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import {
  AppSvgIconComponent,
  BaseOverlayComponent,
  OverlayService,
  SignalTextField,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryListItemResponse } from '@core/api/workspace/models/dataLibraryListItemResponse';
import type { ListDataLibraryApiResponse } from '@core/api/workspace/models/listDataLibraryApiResponse';
import { ListDataLibraryValueType } from '@core/api/workspace/models/listDataLibraryValueType';
import type { ListSystemVariablesApiResponse } from '@core/api/workspace/models/listSystemVariablesApiResponse';
import type { SystemVariableItemResponse } from '@core/api/workspace/models/systemVariableItemResponse';
import { ListSystemVariablesValueTypeItem } from '@core/api/workspace/models/listSystemVariablesValueTypeItem';
import {
  dataLibraryTypeBadgeClass,
  dataLibraryTypeIconClass,
} from '../../../data-library/data-library-type-styles';
import {
  CreateVariableOverlayComponent,
  type CreateVariableOverlayResult,
  type CreateVariableValueType,
} from '../../../data-library/create-variable-overlay/create-variable-overlay';

export type VariablePickerValueType = 'text' | 'number' | 'boolean' | 'date';

export interface VariablePickerResult {
  id: string;
  name: string;
  key: string;
  valueType: VariablePickerValueType;
  /** Raw variable value for field preview defaults. */
  value?: string | number | boolean | null;
  /** Display string for the current variable value (picker list only). */
  valueLabel?: string;
  /** Origin of the selected binding. Defaults to data library. */
  source?: 'dataLibrary' | 'system';
  systemKey?: string;
}

export interface VariablePickerOverlayData {
  /** Non-empty list of variable value types allowed in the picker. */
  valueTypes: VariablePickerValueType[];
}

export type VariablePickerSource = 'formFields' | 'dataLibrary' | 'system';

interface SourceTab {
  id: VariablePickerSource;
  label: string;
  iconSrc: string;
}

const TYPE_ICONS: Record<VariablePickerResult['valueType'], string> = {
  text: 'assets/icons/text-string.svg',
  number: 'assets/icons/numeric.svg',
  date: 'assets/icons/calendar-blank-outline.svg',
  boolean: 'assets/icons/toggle-switch-outline.svg',
};

const SOURCE_TABS: SourceTab[] = [
  {
    id: 'dataLibrary',
    label: 'Data library',
    iconSrc: 'assets/icons/variable.svg',
  },
  {
    id: 'formFields',
    label: 'Form fields',
    iconSrc: 'assets/icons/file-document-outline.svg',
  },
  {
    id: 'system',
    label: 'System variables',
    iconSrc: 'assets/icons/cog-outline.svg',
  },
];

@Component({
  selector: 'app-variable-picker-overlay',
  standalone: true,
  imports: [
    AppSvgIconComponent,
    BaseOverlayComponent,
    SignalTextField,
  ],
  templateUrl: './variable-picker-overlay.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VariablePickerOverlayComponent
  extends BaseComponent
  implements OnInit
{
  private readonly dialogRef = inject(
    DialogRef<VariablePickerResult | null>,
  );
  private readonly dialogData = inject<VariablePickerOverlayData>(DIALOG_DATA);
  private readonly overlayService = inject(OverlayService);
  private readonly userAPIService = inject(WorkspaceAPIService);

  readonly valueTypes: VariablePickerValueType[] =
    this.dialogData.valueTypes?.length > 0
      ? this.dialogData.valueTypes
      : ['text'];
  readonly primaryValueType = this.valueTypes[0];
  readonly sourceTabs = SOURCE_TABS;
  readonly activeSource = signal<VariablePickerSource>('dataLibrary');
  readonly listState = new State<ListDataLibraryApiResponse>();
  readonly systemListState = new State<ListSystemVariablesApiResponse>();
  readonly search = signal('');
  readonly items = signal<VariablePickerResult[]>([]);
  readonly systemItems = signal<VariablePickerResult[]>([]);

  readonly filteredItems = computed(() => {
    const source = this.activeSource();
    if (source !== 'dataLibrary' && source !== 'system') {
      return [];
    }
    const base = source === 'system' ? this.systemItems() : this.items();
    const query = this.search().trim().toLowerCase();
    if (!query) {
      return base;
    }
    return base.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.key.toLowerCase().includes(query) ||
        (item.valueLabel ?? '').toLowerCase().includes(query),
    );
  });

  readonly dataLibraryCount = computed(() => this.items().length);
  readonly systemCount = computed(() => this.systemItems().length);

  readonly isLoading = computed(() => {
    const source = this.activeSource();
    if (source === 'dataLibrary') {
      return this.listState.loading();
    }
    if (source === 'system') {
      return this.systemListState.loading();
    }
    return false;
  });

  readonly isComingSoon = computed(
    () => this.activeSource() === 'formFields',
  );

  readonly isEmpty = computed(() => {
    const source = this.activeSource();
    if (source !== 'dataLibrary' && source !== 'system') {
      return false;
    }
    return !this.isLoading() && this.filteredItems().length === 0;
  });

  readonly showCreateVariable = computed(
    () => this.activeSource() === 'dataLibrary',
  );

  ngOnInit(): void {
    this.loadVariables();
    this.loadSystemVariables();
  }

  setActiveSource(source: VariablePickerSource): void {
    this.activeSource.set(source);
    if (source === 'system' && this.systemItems().length === 0) {
      this.loadSystemVariables();
    }
  }

  sourceCount(source: VariablePickerSource): number {
    if (source === 'dataLibrary') {
      return this.dataLibraryCount();
    }
    if (source === 'system') {
      return this.systemCount();
    }
    return 0;
  }

  typeIconSrc(valueType: VariablePickerResult['valueType']): string {
    return TYPE_ICONS[valueType];
  }

  typeIconClass(valueType: VariablePickerResult['valueType']): string {
    return dataLibraryTypeIconClass(valueType);
  }

  valueBadgeClass(valueType: VariablePickerResult['valueType']): string {
    return dataLibraryTypeBadgeClass(valueType);
  }

  onSearchChange(value: string): void {
    this.search.set(value);
    if (this.activeSource() === 'dataLibrary') {
      this.loadVariables(value);
    }
  }

  selectVariable(item: VariablePickerResult): void {
    this.dialogRef.close(item);
  }

  openCreateVariable(): void {
    this.activeSource.set('dataLibrary');
    const valueType = this.primaryValueType as CreateVariableValueType;
    const createRef = this.overlayService.openModal(
      CreateVariableOverlayComponent,
      {
        data: { valueType },
        panelClass: ['create-variable-overlay-panel'],
      },
    );

    createRef.closed.subscribe(
      (result: CreateVariableOverlayResult | null | undefined) => {
        if (!result) {
          return;
        }
        this.search.set('');
        this.loadVariables('');
      },
    );
  }

  private loadVariables(search = this.search()): void {
    const trimmed = search.trim();
    const singleType =
      this.valueTypes.length === 1 ? this.valueTypes[0] : undefined;
    this.executeRequest({
      state: this.listState,
      request: this.userAPIService.listDataLibrary({
        ...(trimmed ? { search: trimmed } : {}),
        ...(singleType
          ? { valueType: singleType as ListDataLibraryValueType }
          : {}),
        pageNumber: 1,
        pageSize: 100,
      }),
      handleError: true,
      onSuccess: (response: ListDataLibraryApiResponse) => {
        const allowed = new Set(this.valueTypes);
        const mapped = (response.data?.items ?? [])
          .filter(
            (item: DataLibraryListItemResponse) => item.kind === 'variable',
          )
          .map((item: DataLibraryListItemResponse) => this.toResult(item, allowed))
          .filter((item): item is VariablePickerResult => item != null);
        this.items.set(mapped);
      },
    });
  }

  private loadSystemVariables(): void {
    this.executeRequest({
      state: this.systemListState,
      request: this.userAPIService.listSystemVariables({
        valueType: this.valueTypes as ListSystemVariablesValueTypeItem[],
      }),
      handleError: true,
      onSuccess: (response: ListSystemVariablesApiResponse) => {
        const allowed = new Set(this.valueTypes);
        const mapped = (response.data?.items ?? [])
          .map((item) => this.toSystemResult(item, allowed))
          .filter((item): item is VariablePickerResult => item != null);
        this.systemItems.set(mapped);
      },
    });
  }

  private toResult(
    item: DataLibraryListItemResponse,
    allowed: Set<VariablePickerValueType>,
  ): VariablePickerResult | null {
    const valueType = item.valueType;
    if (
      valueType !== 'text' &&
      valueType !== 'number' &&
      valueType !== 'boolean' &&
      valueType !== 'date'
    ) {
      return null;
    }
    if (!allowed.has(valueType)) {
      return null;
    }
    return {
      id: item.id,
      name: item.name,
      key: item.key,
      valueType,
      value: item.value ?? null,
      valueLabel: this.formatValue(item.value),
      source: 'dataLibrary',
    };
  }

  private toSystemResult(
    item: SystemVariableItemResponse,
    allowed: Set<VariablePickerValueType>,
  ): VariablePickerResult | null {
    const valueType = item.valueType;
    if (
      valueType !== 'text' &&
      valueType !== 'number' &&
      valueType !== 'boolean' &&
      valueType !== 'date'
    ) {
      return null;
    }
    if (!allowed.has(valueType)) {
      return null;
    }
    const preview = item.previewValue ?? null;
    return {
      id: item.key,
      name: item.label,
      key: item.key,
      valueType,
      value: preview,
      valueLabel: this.formatValue(preview),
      source: 'system',
      systemKey: item.key,
    };
  }

  private formatValue(value: DataLibraryListItemResponse['value']): string {
    if (value === undefined || value === null || value === '') {
      return '—';
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    return String(value);
  }
}
